/*
  Marketplace application wide settings
*/

import { cleanEnv, str, bool, url } from "envalid";
import type { TAppSettings } from "@hsr/types";

// We have to do this because of how Next.JS handles process.env
const processEnv = {
  //NEXT_PUBLIC_API_URL: process.env.NEXT_PUBLIC_API_URL, // missing
  NEXT_PUBLIC_LANGUAGE_COOKIE_NAME:
    process.env.NEXT_PUBLIC_LANGUAGE_COOKIE_NAME,
  NEXT_PUBLIC_AUTHTOKEN_COOKIE_NAME:
    process.env.NEXT_PUBLIC_AUTHTOKEN_COOKIE_NAME,
  NEXT_PUBLIC_SITECORE_SEARCH_UUID_COOKIE_NAME:
    process.env.NEXT_PUBLIC_SITECORE_SEARCH_UUID_COOKIE_NAME,
  //sitecoreSearchUuidCookieName
  NEXT_PUBLIC_TABLEVIEW_COOKIE_NAME:
    process.env.NEXT_PUBLIC_TABLEVIEW_COOKIE_NAME,
  NEXT_PUBLIC_FALLBACK_LANGUAGE: process.env.NEXT_PUBLIC_FALLBACK_LANGUAGE,
  NEXT_PUBLIC_USE_CACHING: process.env.NEXT_PUBLIC_USE_CACHING,
  NEXT_PUBLIC_USE_LOGGER: process.env.NEXT_PUBLIC_USE_LOGGER,

  NEXT_PUBLIC_GOOGLE_TAG_MANAGER_ID:
    process.env.NEXT_PUBLIC_GOOGLE_TAG_MANAGER_ID,
  NEXT_PUBLIC_RECAPTCHA_KEY: process.env.NEXT_PUBLIC_RECAPTCHA_KEY,
  //RECAPTCHA_SECRET_KEY: process.env.RECAPTCHA_SECRET_KEY,
  NEXT_PUBLIC_USERCENTRICS_SETTINGS_ID:
    process.env.NEXT_PUBLIC_USERCENTRICS_SETTINGS_ID,
  NEXT_PUBLIC_SITECORE_SEARCH_DOMAINID:
    process.env.NEXT_PUBLIC_SITECORE_SEARCH_DOMAINID,

  NEXT_PUBLIC_URL: process.env.NEXT_PUBLIC_URL,
  NEXT_PUBLIC_MYHSR_URL: process.env.NEXT_PUBLIC_MYHSR_URL,
  NEXT_PUBLIC_BFF_ENDPOINT_URL: process.env.NEXT_PUBLIC_BFF_ENDPOINT_URL,
  NEXT_PUBLIC_CHUAT_URL: process.env.NEXT_PUBLIC_CHUAT_URL,
  NEXT_PUBLIC_HEALTHCHECK_ENDPOINT_URL:
    process.env.NEXT_PUBLIC_HEALTHCHECK_ENDPOINT_URL,

  NEXT_PUBLIC_XM_ENDPOINT_PREVIEW_URL:
    process.env.NEXT_PUBLIC_XM_ENDPOINT_PREVIEW_URL,
  NEXT_PUBLIC_XM_PREVIEW_APIKEY: process.env.NEXT_PUBLIC_XM_PREVIEW_APIKEY,

  NEXT_PUBLIC_XM_ENDPOINT_URL: process.env.NEXT_PUBLIC_XM_ENDPOINT_URL,
  NEXT_PUBLIC_XM_APIKEY: process.env.NEXT_PUBLIC_XM_APIKEY,

  NEXT_PUBLIC_CH_ENDPOINT_LABELS_JSON_URL:
    process.env.NEXT_PUBLIC_CH_ENDPOINT_LABELS_JSON_URL,
  NEXT_PUBLIC_CH_ENDPOINT_PREVIEW_URL:
    process.env.NEXT_PUBLIC_CH_ENDPOINT_PREVIEW_URL,
  NEXT_PUBLIC_CH_PREVIEW_APIKEY: process.env.NEXT_PUBLIC_CH_PREVIEW_APIKEY,

  NEXT_PUBLIC_CH_FALLBACKIMAGE_URL:
    process.env.NEXT_PUBLIC_CH_FALLBACKIMAGE_URL,
  NEXT_PUBLIC_CH_ENDPOINT_URL: process.env.NEXT_PUBLIC_CH_ENDPOINT_URL,
  NEXT_PUBLIC_CH_APIKEY: process.env.NEXT_PUBLIC_CH_APIKEY,

  NEXT_PUBLIC_DISCOVER_ENDPOINT_URL:
    process.env.NEXT_PUBLIC_DISCOVER_ENDPOINT_URL,
  NEXT_PUBLIC_DISCOVER_APIKEY: process.env.NEXT_PUBLIC_DISCOVER_APIKEY,
  NEXT_PUBLIC_SEARCHEVENTS_ENDPOINT_URL:
    process.env.NEXT_PUBLIC_SEARCHEVENTS_ENDPOINT_URL,

  NEXT_PUBLIC_SITEMAP_PRODUCTS_GERMAN_URL:
    process.env.NEXT_PUBLIC_SITEMAP_PRODUCTS_GERMAN_URL,
  NEXT_PUBLIC_SITEMAP_PRODUCTS_ENGLISH_URL:
    process.env.NEXT_PUBLIC_SITEMAP_PRODUCTS_ENGLISH_URL
};

// No customization needed here
const validators = {
  // urls
  NEXT_PUBLIC_URL: url(),
  NEXT_PUBLIC_MYHSR_URL: url(),
  NEXT_PUBLIC_CHUAT_URL: url(),
  NEXT_PUBLIC_SITEMAP_PRODUCTS_GERMAN_URL: url(),
  NEXT_PUBLIC_SITEMAP_PRODUCTS_ENGLISH_URL: url(),
  // BFF
  NEXT_PUBLIC_BFF_ENDPOINT_URL: url(),
  NEXT_PUBLIC_HEALTHCHECK_ENDPOINT_URL: url(),
  // XM Cloud
  NEXT_PUBLIC_XM_ENDPOINT_URL: url(),
  NEXT_PUBLIC_XM_APIKEY: str(),
  // XM Cloud Preview
  NEXT_PUBLIC_XM_ENDPOINT_PREVIEW_URL: url(),
  NEXT_PUBLIC_XM_PREVIEW_APIKEY: str(),
  // Content Hub
  NEXT_PUBLIC_CH_ENDPOINT_URL: url(),
  NEXT_PUBLIC_CH_APIKEY: str(),
  // Content Hub Preview
  NEXT_PUBLIC_CH_ENDPOINT_LABELS_JSON_URL: str(),
  NEXT_PUBLIC_CH_ENDPOINT_PREVIEW_URL: url(),
  NEXT_PUBLIC_CH_PREVIEW_APIKEY: str(),
  NEXT_PUBLIC_CH_FALLBACKIMAGE_URL: url(),
  // Discover
  NEXT_PUBLIC_DISCOVER_ENDPOINT_URL: url(),
  NEXT_PUBLIC_DISCOVER_APIKEY: str(),
  // Sitecore Search Events
  NEXT_PUBLIC_SEARCHEVENTS_ENDPOINT_URL: url(),
  // cookies
  NEXT_PUBLIC_LANGUAGE_COOKIE_NAME: str(),
  NEXT_PUBLIC_AUTHTOKEN_COOKIE_NAME: str(),
  NEXT_PUBLIC_TABLEVIEW_COOKIE_NAME: str(),
  NEXT_PUBLIC_FALLBACK_LANGUAGE: str(),
  NEXT_PUBLIC_SITECORE_SEARCH_UUID_COOKIE_NAME: str(),
  // keys
  NEXT_PUBLIC_RECAPTCHA_KEY: str(),
  //RECAPTCHA_SECRET_KEY: str(),
  NEXT_PUBLIC_GOOGLE_TAG_MANAGER_ID: str(),
  NEXT_PUBLIC_USERCENTRICS_SETTINGS_ID: str(),
  NEXT_PUBLIC_SITECORE_SEARCH_DOMAINID: str(),
  // cache
  NEXT_PUBLIC_USE_CACHING: bool(),
  // logger
  NEXT_PUBLIC_USE_LOGGER: bool()
};

const env = cleanEnv(processEnv, validators, {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  reporter: ({ errors, env }) => {
    for (const [envVar, err] of Object.entries(errors)) {
      throw new Error(
        `Please provide valid value for required environment variable: ${envVar}=${err.message} and then restart the server so that the changes can take effect.`
      );
    }
  }
});

// No customization needed here
export const appSettings: TAppSettings = {
  useCaching: env.NEXT_PUBLIC_USE_CACHING,
  useLogger: env.NEXT_PUBLIC_USE_LOGGER,
  languageCookieName: env.NEXT_PUBLIC_LANGUAGE_COOKIE_NAME,
  sitecoreSearchUuidCookieName:
    env.NEXT_PUBLIC_SITECORE_SEARCH_UUID_COOKIE_NAME,
  authTokenCookieName: env.NEXT_PUBLIC_AUTHTOKEN_COOKIE_NAME,
  tblViewCookieName: env.NEXT_PUBLIC_TABLEVIEW_COOKIE_NAME,
  fallbackLanguage: env.NEXT_PUBLIC_FALLBACK_LANGUAGE,
  publicUrl: env.NEXT_PUBLIC_URL,
  publicMyHsrUrl: env.NEXT_PUBLIC_MYHSR_URL,
  publicChUatUrl: env.NEXT_PUBLIC_CHUAT_URL, // hier
  googleTagManagerId: env.NEXT_PUBLIC_GOOGLE_TAG_MANAGER_ID,
  usercentricsId: env.NEXT_PUBLIC_USERCENTRICS_SETTINGS_ID,
  bffEndpointUrl: env.NEXT_PUBLIC_BFF_ENDPOINT_URL,
  bffEndpointHealthUrl: env.NEXT_PUBLIC_HEALTHCHECK_ENDPOINT_URL,
  // XM Cloud
  xmcPublishEndpointUrl: env.NEXT_PUBLIC_XM_ENDPOINT_URL,
  xmcPublishGraphApiKey: env.NEXT_PUBLIC_XM_APIKEY,
  // XM Cloud Preview
  xmcPreviewEndpointUrl: env.NEXT_PUBLIC_XM_ENDPOINT_PREVIEW_URL,
  xmcPreviewGraphApiKey: env.NEXT_PUBLIC_XM_PREVIEW_APIKEY,
  // Content Hub
  chPublishEndpointUrl: env.NEXT_PUBLIC_CH_ENDPOINT_URL,
  chPublishGraphApiKey: env.NEXT_PUBLIC_CH_APIKEY,
  // Content Hub Labels JSON
  chLabelsJsonUrl: env.NEXT_PUBLIC_CH_ENDPOINT_LABELS_JSON_URL,
  // Content Hub Preview
  chPreviewEndpointUrl: env.NEXT_PUBLIC_CH_ENDPOINT_PREVIEW_URL,
  chPreviewGraphApiKey: env.NEXT_PUBLIC_CH_PREVIEW_APIKEY,
  // Fallback Image
  chFallbackImageUrl: env.NEXT_PUBLIC_CH_FALLBACKIMAGE_URL,
  // Discover
  discoverPublishEndpointUrl: env.NEXT_PUBLIC_DISCOVER_ENDPOINT_URL,
  discoverPublishApiKey: env.NEXT_PUBLIC_DISCOVER_APIKEY,
  // Sitecore Search Events
  searchEventsEndpointUrl: env.NEXT_PUBLIC_SEARCHEVENTS_ENDPOINT_URL,
  // Sitemap
  sitemapProductsGermanUrl: env.NEXT_PUBLIC_SITEMAP_PRODUCTS_GERMAN_URL,
  sitemapProductsEnglishUrl: env.NEXT_PUBLIC_SITEMAP_PRODUCTS_ENGLISH_URL,
  // use this api endpoint in your application (depending on the environment)
  xmcEndpoint: "",
  xmcGraphApiKey: "",
  chEndpoint: "",
  chGraphApiKey: "",
  discoverEndpointUrl: "",
  discoverApiKey: "",
  sitecoreSearchDomainId: env.NEXT_PUBLIC_SITECORE_SEARCH_DOMAINID,
  useSitecoreSearchEvents: true,
  useSitecoreSearchWidget: true
};

// Endpoints and api keys based on the environment
switch (process.env.NODE_ENV) {
  // added 16.1.25
  case "development":
    appSettings.useSitecoreSearchEvents = false;
    appSettings.useSitecoreSearchWidget = false;

    // changed to publish 16.1.25
    appSettings.xmcEndpoint = appSettings.xmcPublishEndpointUrl;
    appSettings.xmcGraphApiKey = appSettings.xmcPublishGraphApiKey;

    // changed to publish 16.1.25
    appSettings.chEndpoint = appSettings.chPublishEndpointUrl;
    appSettings.chGraphApiKey = appSettings.chPublishGraphApiKey;

    appSettings.discoverEndpointUrl = appSettings.discoverPublishEndpointUrl;
    appSettings.discoverApiKey = appSettings.discoverPublishApiKey;
    break;
  case "production":
    appSettings.useCaching = true;
    appSettings.useSitecoreSearchEvents = true;
    appSettings.xmcEndpoint = appSettings.xmcPublishEndpointUrl;
    appSettings.xmcGraphApiKey = appSettings.xmcPublishGraphApiKey;

    appSettings.chEndpoint = appSettings.chPublishEndpointUrl;
    appSettings.chGraphApiKey = appSettings.chPublishGraphApiKey;

    appSettings.discoverEndpointUrl = appSettings.discoverPublishEndpointUrl;
    appSettings.discoverApiKey = appSettings.discoverPublishApiKey;
    break;
  // should TEST, ACCEPTANCE
  default:
    appSettings.useSitecoreSearchEvents = false;
    appSettings.useSitecoreSearchWidget = false;

    appSettings.xmcEndpoint = appSettings.xmcPreviewEndpointUrl;
    appSettings.xmcGraphApiKey = appSettings.xmcPreviewGraphApiKey;

    appSettings.chEndpoint = appSettings.chPreviewEndpointUrl;
    appSettings.chGraphApiKey = appSettings.chPreviewGraphApiKey;

    appSettings.discoverEndpointUrl = appSettings.discoverPublishEndpointUrl;
    appSettings.discoverApiKey = appSettings.discoverPublishApiKey;
}
